// useAutomationRuleActionsState.js

import { useCallback, useMemo, useState } from 'react';

// Utilities
import {
  buildDefaultAction,
  buildDefaultEmailAction,
} from 'collection/automations/utils/automationRuleUtils';
import { getAllowedValueModes } from 'collection/automations/utils/automationValueUtils';
import {
  normalizeActionValueType,
  normalizeRecipientSource,
} from '../utils/automationActionEditorUtils';

// Hooks
import useRuleActionUpdater from './useRuleActionUpdater';

/**
 * useAutomationRuleActionsState
 *
 * Manages row update helpers, action type transitions, and recipient picker state for AutomationRuleActions.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.rule - Parent rule containing action rows.
 * @param {array} params.actions - Action rows to render.
 * @param {Map} params.fieldTypeByKey - Field type lookup keyed by field key.
 * @param {boolean} [params.allowEmailActions=true] - Whether send-email actions can be selected.
 * @param {function} params.updateRule - Updates a rule by key with an updater callback.
 *
 * @returns {object} Grouped action state and handlers.
 */
const useAutomationRuleActionsState = ({
  rule,
  actions,
  fieldTypeByKey,
  allowEmailActions = true,
  updateRule,
}) => {
  const [recipientModalActionKey, setRecipientModalActionKey] = useState(null);

  const { updateAction, replaceAction } = useRuleActionUpdater({
    ruleKey: rule.key,
    updateRule,
  });

  const normalizedActions = useMemo(() => {
    return Array.isArray(actions) ? actions : [];
  }, [actions]);

  const recipientModalAction = useMemo(() => {
    if (!recipientModalActionKey) return null;

    return normalizedActions.find((entry) => entry?.key === recipientModalActionKey) || null;
  }, [normalizedActions, recipientModalActionKey]);

  /**
   * handleActionTypeChange
   *
   * Swaps an action row between supported action types while preserving set-field state when requested.
   *
   * @param {string} actionKey - Action row key to update.
   * @param {string} nextType - Next action type to apply.
   * @param {object} [options={}] - Transition options.
   * @param {boolean} [options.preserveSetFieldState=false] - Whether to preserve the current set-field value config.
   * @returns {void}
   */
  const handleActionTypeChange = useCallback((actionKey, nextType, options = {}) => {
    const shouldPreserveSetFieldState = Boolean(options?.preserveSetFieldState);

    if (nextType === 'send_email') {
      if (!allowEmailActions) return;

      replaceAction(actionKey, () => buildDefaultEmailAction());
      return;
    }

    if (nextType !== 'set_field') return;

    if (!shouldPreserveSetFieldState) {
      replaceAction(actionKey, (_, prevRule) => buildDefaultAction({
        databaseKey: prevRule?.scope?.databaseKey,
        typeKey: prevRule?.scope?.nodeTypeKey,
      }));
      return;
    }

    replaceAction(actionKey, (entry, prevRule) => {
      const preservedFieldKey = String(entry?.fieldKey || '').trim();
      const nextFieldType = fieldTypeByKey.get(preservedFieldKey) || 'string';
      const nextAllowedModes = getAllowedValueModes(nextFieldType);
      const normalizedValueType = normalizeActionValueType(entry?.value?.type, nextAllowedModes);

      return {
        ...buildDefaultAction({
          databaseKey: prevRule?.scope?.databaseKey,
          typeKey: prevRule?.scope?.nodeTypeKey,
        }),
        fieldKey: preservedFieldKey,
        value: normalizedValueType === 'literal'
          || normalizedValueType === 'add'
          || normalizedValueType === 'remove'
          ? {
            type: normalizedValueType,
            literal: entry?.value?.literal ?? '',
          }
          : { type: normalizedValueType },
      };
    });
  }, [allowEmailActions, fieldTypeByKey, replaceAction]);

  /**
   * handleRecipientModalOpen
   *
   * Opens the recipient picker for the selected action row.
   *
   * @param {string} actionKey - Action row key to edit.
   * @returns {void}
   */
  const handleRecipientModalOpen = useCallback((actionKey) => {
    setRecipientModalActionKey(actionKey);
  }, []);

  /**
   * handleRecipientModalClose
   *
   * Closes the recipient picker modal.
   *
   * @returns {void}
   */
  const handleRecipientModalClose = useCallback(() => {
    setRecipientModalActionKey(null);
  }, []);

  /**
   * handleRecipientChange
   *
   * Applies explicit members to the active send-email recipients or set-field
   * people literal, depending on which action opened the picker.
   *
   * @param {array} people - Selected member-like values from the picker.
   * @returns {void}
   */
  const handleRecipientChange = useCallback((people) => {
    if (!recipientModalAction?.key) return;

    const nextPeople = Array.from(new Map(
      (Array.isArray(people) ? people : [])
        .map((person) => {
          const userKey = String(person?.userKey || person?.key || '').trim();
          if (!userKey) return null;
          return [userKey, { key: userKey, userKey }];
        })
        .filter(Boolean),
    ).values());

    const actionType = String(recipientModalAction?.type || '').trim().toLowerCase() || 'set_field';

    if (actionType === 'send_email') {
      updateAction(recipientModalAction.key, (entry) => ({
        ...entry,
        recipientSource: {
          ...normalizeRecipientSource(entry),
          type: 'explicit_members',
          explicitMemberUserKeys: nextPeople.map((person) => person.userKey),
        },
      }));
    } else {
      updateAction(recipientModalAction.key, (entry) => ({
        ...entry,
        value: {
          type: String(entry?.value?.type || 'literal').trim().toLowerCase() || 'literal',
          literal: nextPeople,
        },
      }));
    }

    handleRecipientModalClose();
  }, [
    handleRecipientModalClose,
    recipientModalAction?.key,
    recipientModalAction?.type,
    updateAction,
  ]);

  return {
    actionState: {
      normalizedActions,
      recipientModalActionKey,
      recipientModalAction,
    },
    actionHandlers: {
      updateAction,
      handleActionTypeChange,
      handleRecipientModalOpen,
      handleRecipientModalClose,
      handleRecipientChange,
    },
  };
};

export default useAutomationRuleActionsState;